// Parse and re-emit pitch atoms as they appear in mini-notation
// (e.g. "c4", "eb3", "F#5"). Used by counterMelody to walk a voice's
// MiniPattern and shift each pitch atom by a semitone interval while
// leaving rest/group/non-pitch atoms (sample names, chord symbols)
// untouched.
//
// Strudel accepts both pitch-class notation ("c4") and MIDI numbers
// ("60") inside note(); we handle both.

import { canonicalRoot, ROOTS } from '../blocks/tonal/parse';

export type ParsedPitch = {
  /** Canonical root spelling, e.g. "C", "Eb". */
  root: string;
  /** Octave number — Strudel uses scientific octaves (middle C = c4). */
  octave: number;
};

const PITCH_NAME_RE = /^([A-Ga-g][#b]?)(-?\d+)$/;

/** Try to parse "c4", "Eb3", "F#5". Returns null for anything else
 *  (sample names like "bd", chord symbols like "Cm7", MIDI ints, rests). */
export function parsePitchName(name: string): ParsedPitch | null {
  const m = name.match(PITCH_NAME_RE);
  if (!m) return null;
  const rootRaw = m[1]!;
  // Mini-notation lower-cases the letter; tonal/parse expects upper.
  const upper = rootRaw[0]!.toUpperCase() + rootRaw.slice(1);
  const root = canonicalRoot(upper);
  if (!root) return null;
  const oct = Number(m[2]!);
  if (!Number.isInteger(oct)) return null;
  return { root, octave: oct };
}

/** Format a parsed pitch back to mini-notation form. Always lower-case
 *  the root letter — matches the dominant style in the harness's
 *  default songs and what the parser hands back on round-trip. */
export function formatPitch(pitch: ParsedPitch): string {
  return pitch.root[0]!.toLowerCase() + pitch.root.slice(1) + pitch.octave;
}

/** Combined semitone index — useful when transposing. */
export function pitchToSemitones(p: ParsedPitch): number {
  const idx = (ROOTS as readonly string[]).indexOf(p.root);
  return p.octave * 12 + idx;
}

/** Inverse of pitchToSemitones. Always picks the canonical (sharp-side)
 *  spelling — there's no enharmonic context to choose flat. */
export function semitonesToPitch(semitones: number): ParsedPitch {
  const wrapped = ((semitones % 12) + 12) % 12;
  return {
    root: ROOTS[wrapped]!,
    octave: Math.floor(semitones / 12),
  };
}

/** Transpose by N semitones (sign-preserving). */
export function transposePitch(p: ParsedPitch, semitones: number): ParsedPitch {
  return semitonesToPitch(pitchToSemitones(p) + semitones);
}
